import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, Package, Phone, Info, Briefcase, Home } from "lucide-react";
import SendtooLogo from "@/components/shared/SendtooLogo";

const navLinks = [
  { to: "/", label: "Home", icon: Home },
  { to: "/about", label: "About", icon: Info },
  { to: "/services", label: "Services", icon: Briefcase },
  { to: "/track", label: "Track", icon: Package },
  { to: "/contact", label: "Contact", icon: Phone },
];

export default function PublicLayout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 12);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-white font-body">
      {/* Navbar */}
      <nav className="fixed top-0 left-0 right-0 z-50 px-3 sm:px-6 pt-3 pointer-events-none">
        <div
          className={`pointer-events-auto max-w-6xl mx-auto px-4 sm:px-5 rounded-2xl border border-white/10 backdrop-blur-xl backdrop-saturate-150 transition-all duration-300 ${
            scrolled
              ? "bg-[#0A183D]/90 shadow-[0_10px_40px_rgba(2,8,24,0.45)]"
              : "bg-[#0A183D]/40 shadow-[0_8px_30px_rgba(2,8,24,0.25)]"
          }`}
        >
          <div className="flex items-center justify-between h-14">
            <SendtooLogo to="/" size="lg-header" variant="light" className="!h-10 sm:!h-12 !max-w-[200px] -ml-1" />

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`px-4 py-2 text-sm font-medium rounded-full border border-transparent transition-all duration-200 ease-out ${
                    location.pathname === link.to
                      ? "text-[#159DFF] bg-[#159DFF]/10"
                      : "text-white/80 hover:text-white hover:bg-white/10"
                  } ${
                    link.label === "Track"
                      ? "hover:!text-[#159DFF] hover:bg-[#159DFF]/[0.12] hover:border-[#159DFF]/[0.45] hover:shadow-[0_0_18px_rgba(21,157,255,0.16)] hover:scale-[1.04] focus-visible:!text-[#159DFF] focus-visible:bg-[#159DFF]/[0.12] focus-visible:border-[#159DFF]/[0.45] focus-visible:shadow-[0_0_18px_rgba(21,157,255,0.16)] focus-visible:scale-[1.04] focus-visible:outline-none"
                      : ""
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="hidden md:flex items-center gap-3">
              <Link
                to="/auth/login"
                data-testid="nav-login-btn"
                className="px-5 py-2 text-sm font-semibold rounded-full transition-colors uppercase tracking-wide border border-white/40 text-white hover:bg-white/10"
              >
                Login
              </Link>
              <Link
                to="/auth/register"
                data-testid="nav-register-btn"
                className="px-5 py-2 text-sm font-semibold text-white bg-[#159DFF] rounded-full hover:bg-[#0F84D9] transition-colors uppercase tracking-wide"
              >
                Sign Up
              </Link>
            </div>

            {/* Mobile menu button */}
            <button
              data-testid="mobile-menu-btn"
              className="md:hidden p-2 rounded-full text-white"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {menuOpen && (
          <div className="pointer-events-auto md:hidden mt-2 max-w-6xl mx-auto rounded-2xl border border-white/10 bg-[#0A183D]/95 backdrop-blur-xl px-4 pb-4 pt-2">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="flex items-center gap-3 py-3 text-sm font-medium text-white/90 border-b border-white/10"
                onClick={() => setMenuOpen(false)}
              >
                <link.icon className="w-4 h-4 text-[#159DFF]" />
                {link.label}
              </Link>
            ))}
            <div className="flex gap-3 mt-4">
              <Link to="/auth/login" className="flex-1 text-center py-2 text-sm font-semibold text-white border-2 border-white/40 rounded-sm uppercase">Login</Link>
              <Link to="/auth/register" className="flex-1 text-center py-2 text-sm font-semibold text-white bg-[#159DFF] rounded-sm uppercase">Sign Up</Link>
            </div>
          </div>
        )}
      </nav>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-[#0A183D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <SendtooLogo size="xl" variant="light" className="mb-4" />
              <p className="text-slate-400 text-sm leading-relaxed">
                The Reliable Kinetic Network. Moving business, not just boxes. Fast, secure, and transparent logistics for modern India.
              </p>
            </div>
            <div>
              <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-slate-300">Quick Links</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                {navLinks.map(l => <li key={l.to}><Link to={l.to} className="hover:text-white transition-colors">{l.label}</Link></li>)}
              </ul>
            </div>
            <div>
              <h4 className="font-heading font-semibold text-sm uppercase tracking-wider mb-4 text-slate-300">Portals</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/auth/login" className="hover:text-white transition-colors">Customer Login</Link></li>
                <li><Link to="/auth/login" className="hover:text-white transition-colors">Agent Login</Link></li>
                <li><Link to="/auth/login" className="hover:text-white transition-colors">Admin Panel</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-700 mt-10 pt-6 text-center text-xs text-slate-500">
            © {new Date().getFullYear()} Sendtoo Logistics. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
